import { useState } from 'react';
import { api } from '../api/client';

const FAQS: { section: string; color: string; items: { q: string; a: string }[] }[] = [
  {
    section: 'Getting started',
    color: 'indigo',
    items: [
      {
        q: 'What is EventsCarousel?',
        a: 'EventsCarousel is a private family archive where you can record events, upload photos, tag the people in them, and attach voice recordings. Everything stays in one place so future generations can explore your family history.',
      },
      {
        q: 'How do I create my first event?',
        a: 'Click "New Event" (the + button in the top right of the events page). Give it a name, then add photos — EventsCarousel will automatically read the date and location from your photo\'s metadata to pre-fill those fields. Add the people who were there, then click "Create Event".',
      },
      {
        q: 'How do I add people to My Circle?',
        a: 'Go to My Circle in the top nav. Click "Add a person" and enter their name and relationship. Choose whether they get Full Access (can view and add photos, voice recordings, and stories) or View Only (can just look), and whether that covers your whole archive or only specific events you pick. If you provide an email address, they\'ll receive an invitation to create their own EventsCarousel account.',
      },
    ],
  },
  {
    section: 'Photos & media',
    color: 'violet',
    items: [
      {
        q: 'How do I add photos to an event?',
        a: 'When creating a new event, click "Add Photos" right after entering the event name. You can select multiple photos at once. On mobile, you can also use your camera directly. Photos are uploaded when you click "Create Event".',
      },
      {
        q: 'Can EventsCarousel detect who is in a photo automatically?',
        a: 'The app can spot faces in a photo to make tagging easier (tap a face to mark who it is), but it doesn’t try to recognize or remember whose face is whose — you always pick the name yourself. Visit the "Faces" page for a quick way to find and tag photos that are still missing someone from your circle. Tagged people can see those photos when they log in.',
      },
      {
        q: 'Why did the date and location fill in automatically when I added a photo?',
        a: 'Modern cameras and phones embed the time and GPS location inside each photo\'s EXIF metadata. EventsCarousel reads this and pre-fills the event date and location fields for you. You can always change them if they\'re wrong or missing.',
      },
      {
        q: 'How do voice recordings work?',
        a: 'Open any event and click "Record Voice". EventsCarousel records audio directly from your microphone and attaches it to the event — no app needed. Voice memos are great for capturing the story behind the photos in the person\'s own words.',
      },
    ],
  },
  {
    section: 'Slideshows & bulk import',
    color: 'rose',
    items: [
      {
        q: 'I have hundreds of photos to add — do I really have to create events one at a time?',
        a: 'No — use Bulk Import (the button next to "+ Add Event"). Pick up to 300 photos at once and EventsCarousel automatically groups them into candidate events by date and location, picks out the best few photos from each group, and lets you review and edit everything before anything is created.',
      },
      {
        q: 'What is a Milestone Slideshow?',
        a: 'A Milestone Slideshow tells one person\'s story in order — every photo they\'re tagged in, from the earliest to the most recent (or any date range you choose), pulled automatically from across your whole archive. Find it under "✨ Milestone Slideshow" next to Bulk Import. You can save it, share a link so friends and family can contribute their own photos to it (you approve each one before it\'s added), and play it any time.',
      },
      {
        q: 'Can I add background music to a slideshow?',
        a: 'Yes — click the 🎵 button in any slideshow\'s top bar to pick from a small set of built-in tracks, or turn music off entirely. If you don\'t pick one, a default track plays automatically. Music softens automatically whenever a slide\'s own voice recording is playing, then eases back up once it ends. For a Milestone Slideshow, your choice is saved and remembered next time; for other slideshows, it\'s remembered on this device.',
      },
    ],
  },
  {
    section: 'Sharing & access',
    color: 'emerald',
    items: [
      {
        q: 'What\'s the difference between Full Access and View Only?',
        a: 'Full Access means someone can see an event and add their own photos, captions, and voice recordings to it. View Only means they can look but can\'t add anything. You choose which one when you add someone to My Circle, and you can change it any time from their row on that page.',
      },
      {
        q: 'Can I give someone access to just a few events instead of my whole archive?',
        a: 'Yes — this works the same way for both Full Access and View Only. When adding someone (or editing an existing person in My Circle), choose "Specific events" instead of "All events", then pick exactly which ones. You can also flip an individual event\'s access for someone from that event\'s Share button. Switching back to "All events" later doesn\'t lose your picks — they\'re kept in case you switch back to "Specific events".',
      },
      {
        q: 'How do I ask someone to help complete an event?',
        a: 'Open the event and click "Ask to contribute" (shown on events you can edit). Pick someone with Full Access, then choose what you\'re asking for — a few of their best photos (you pick how many), a short voice memory, or a bit of context or a story — and send it. They\'ll get an email and a quiet progress reminder on the event page; there\'s no pressure or re-asking if they don\'t get to it.',
      },
      {
        q: 'Can I share an event with people outside EventsCarousel, like extended family with no account?',
        a: 'Yes — click "Public link" on any event to generate a read-only link that works without logging in. Anyone with that exact link can view the event\'s photos, voice recordings, and comments. You can make it private again at any time by clicking the small revoke icon that appears next to "Copy link" once a link exists.',
      },
      {
        q: 'What are Delegates?',
        a: 'Delegates are people you trust to help manage your archive — like a child helping an elderly parent. A Delegate can view and edit your events as if they were you, using the account switcher in the top nav.',
      },
      {
        q: 'How do I give someone Delegate access?',
        a: 'Go to Settings and find the Delegates section, then enter the email address of the person you\'d like to grant access to. They\'ll receive an invitation. Once accepted, they\'ll see your archive listed in their account switcher.',
      },
      {
        q: 'Can people outside my family see my events?',
        a: 'By default, no — only you, your Delegates, and the people you\'ve given access to in My Circle can see your archive, and nothing is publicly searchable. The one exception is if you generate a Public Link for a specific event (see above): anyone with that exact link can view it, even without an account, until you revoke it.',
      },
    ],
  },
  {
    section: 'Account & settings',
    color: 'amber',
    items: [
      {
        q: 'How do I change my display name or email?',
        a: 'Click your avatar in the top right to go to Settings. From there you can update your display name. Email changes require re-verification and can be done from the same page.',
      },
      {
        q: 'How do I enable two-factor authentication?',
        a: 'Go to Settings and look for the Two-Factor Authentication section. Click "Set up 2FA" and scan the QR code with an authenticator app (like Google Authenticator or Authy). You\'ll need to enter a code to confirm.',
      },
      {
        q: 'How do I browse events in the carousel flip-book view?',
        a: 'Click the Carousel icon in the top nav (or tap "Carousel" in the mobile menu). This shows your events laid out like a photo album you can flip through page by page.',
      },
    ],
  },
];

const BADGE: Record<string, string> = {
  indigo: 'bg-indigo-50 text-indigo-700',
  violet: 'bg-violet-50 text-violet-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  amber: 'bg-amber-50 text-amber-700',
  rose: 'bg-rose-50 text-rose-700',
};
const BORDER: Record<string, string> = {
  indigo: 'border-indigo-100',
  violet: 'border-violet-100',
  emerald: 'border-emerald-100',
  amber: 'border-amber-100',
  rose: 'border-rose-100',
};

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        className="w-full text-left flex justify-between items-start gap-4 py-4 text-sm font-medium text-slate-800 hover:text-indigo-700 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <span>{q}</span>
        <svg
          className={`w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"
        >
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>
      {open && (
        <p className="pb-4 text-sm text-slate-600 leading-relaxed">{a}</p>
      )}
    </div>
  );
}

type FeedbackType = 'bug' | 'feature' | 'general';

const TYPE_LABELS: { value: FeedbackType; label: string; desc: string }[] = [
  { value: 'bug',     label: 'Bug report',      desc: 'Something isn\'t working' },
  { value: 'feature', label: 'Feature request',  desc: 'An idea or improvement' },
  { value: 'general', label: 'General feedback', desc: 'Anything else' },
];

export function Help() {
  const [type, setType] = useState<FeedbackType>('general');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    setStatus('sending');
    setErrorMsg('');
    try {
      await api.post('/feedback', { type, message });
      setStatus('sent');
      setMessage('');
    } catch (err: unknown) {
      setErrorMsg((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to send. Please try again.');
      setStatus('error');
    }
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-1">Help & Feedback</h1>
        <p className="text-sm text-slate-500">Common questions, and a way to reach us directly.</p>
      </div>

      {/* FAQ sections */}
      <div className="space-y-5 mb-10">
        {FAQS.map(({ section, color, items }) => (
          <div key={section} className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${BORDER[color]}`}>
            <div className={`px-5 py-3 border-b ${BORDER[color]}`}>
              <span className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${BADGE[color]}`}>
                {section}
              </span>
            </div>
            <div className="px-5">
              {items.map(item => (
                <FaqItem key={item.q} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Feedback form */}
      <div className="bg-white rounded-2xl border border-indigo-50 shadow-sm p-6">
        <h2 className="text-base font-bold text-slate-900 mb-1">Send feedback</h2>
        <p className="text-sm text-slate-500 mb-5">Found a bug or have an idea? We'd love to hear from you.</p>

        {status === 'sent' ? (
          <div className="flex flex-col items-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-800">Thank you — feedback received!</p>
            <p className="text-xs text-slate-500 mt-1">We'll review it and get back to you if needed.</p>
            <button
              onClick={() => setStatus('idle')}
              className="mt-4 text-xs text-indigo-600 hover:underline"
            >
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type selector */}
            <div className="flex gap-2 flex-wrap">
              {TYPE_LABELS.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  className={`flex flex-col items-start px-3.5 py-2.5 rounded-xl border text-left transition-all ${
                    type === t.value
                      ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <span className="text-xs font-semibold">{t.label}</span>
                  <span className="text-xs opacity-70 mt-0.5">{t.desc}</span>
                </button>
              ))}
            </div>

            {/* Message */}
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={5}
              placeholder={
                type === 'bug'
                  ? 'Describe what happened and the steps to reproduce it…'
                  : type === 'feature'
                  ? 'Describe the feature and why it would be useful…'
                  : 'Tell us what\'s on your mind…'
              }
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />

            {status === 'error' && (
              <p className="text-xs text-red-600">{errorMsg}</p>
            )}

            <button
              type="submit"
              disabled={!message.trim() || status === 'sending'}
              className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {status === 'sending' ? 'Sending…' : 'Send feedback'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
